import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import postBrowseFeedback, {
  BrowseFeedbackSentiment,
} from '@cartwhl/common/api/postBrowseFeedback';
import { BrowseResultItem } from '@cartwhl/common/types';
import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

import { noticesStore, sceneSettingsStore } from 'stores';

import { useLibraryStore } from '../useLibraryStore';

type FootContact = {
  start: number;
  end: number;
};

type Props = {
  browseItem: BrowseResultItem;
  onFeedbackSubmitted?: () => void;
  className?: string;
};

export default function BrowseRecordFootContacts({
  browseItem,
  onFeedbackSubmitted,
  className = '',
}: Props) {
  const [leftContacts, setLeftContacts] = useState<FootContact[]>([]);
  const [rightContacts, setRightContacts] = useState<FootContact[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leftStart, setLeftStart] = useState('');
  const [leftEnd, setLeftEnd] = useState('');
  const [rightStart, setRightStart] = useState('');
  const [rightEnd, setRightEnd] = useState('');
  const [editingIndex, setEditingIndex] = useState<{
    side: 'left' | 'right';
    index: number;
  } | null>(null);
  const { addFootContacts } = useLibraryStore();
  const {
    playbackSettings: { frameRate },
  } = useSnapshot(sceneSettingsStore.state);
  const isValidRate = frameRate === 24;

  useEffect(() => {
    if (!isValidRate) {
      noticesStore.error(
        'Foot contact recording is only available at 24 FPS. Please change the frame rate to 24 FPS.',
      );
    }
  }, [isValidRate]);

  // Sort contacts by start frame
  const sortedLeftContacts = [...leftContacts].sort(
    (a, b) => a.start - b.start,
  );
  const sortedRightContacts = [...rightContacts].sort(
    (a, b) => a.start - b.start,
  );

  const validateRange = (
    start: number,
    end: number,
    existingRanges: FootContact[],
    excludeIndex?: number,
  ): boolean => {
    // Ensure start is less than end
    if (start >= end) return false;

    // Ensure non-negative numbers
    if (start < 0 || end < 0) return false;

    // Check for overlaps with existing ranges, excluding the one being edited
    for (let i = 0; i < existingRanges.length; i++) {
      if (i === excludeIndex) continue;

      const range = existingRanges[i];
      if (
        (start >= range.start && start <= range.end) ||
        (end >= range.start && end <= range.end) ||
        (start <= range.start && end >= range.end)
      ) {
        return false;
      }
    }

    return true;
  };

  const addContact = (side: 'left' | 'right') => {
    const start = side === 'left' ? leftStart : rightStart;
    const end = side === 'left' ? leftEnd : rightEnd;

    if (!start || !end) {
      noticesStore.error('Please enter both start and end frames');
      return;
    }

    const startFrame = parseInt(start);
    const endFrame = parseInt(end);

    if (isNaN(startFrame) || isNaN(endFrame)) {
      noticesStore.error('Please enter valid numbers');
      return;
    }

    if (startFrame <= 0 || endFrame <= 0) {
      noticesStore.error(
        'Frame numbers must be non-negative and greater than 0',
      );
      return;
    }

    if (startFrame >= 1000 || endFrame >= 1000) {
      noticesStore.error(
        'Make sure your start and end frame limits are within the length of the animation',
      );
      return;
    }

    const existingRanges = side === 'left' ? leftContacts : rightContacts;
    const excludeIndex =
      editingIndex?.side === side ? editingIndex.index : undefined;
    const isValid = validateRange(
      startFrame,
      endFrame,
      existingRanges,
      excludeIndex,
    );

    if (!isValid) {
      noticesStore.error(
        'Invalid range: Either start is not less than end, or there is an overlap with existing ranges',
      );
      return;
    }

    const newContact = { start: startFrame, end: endFrame };
    if (side === 'left') {
      if (editingIndex?.side === 'left') {
        const updatedContacts = [...leftContacts];
        updatedContacts[editingIndex.index] = newContact;
        setLeftContacts(updatedContacts);
      } else {
        setLeftContacts([...leftContacts, newContact]);
      }
      setLeftStart('');
      setLeftEnd('');
    } else {
      if (editingIndex?.side === 'right') {
        const updatedContacts = [...rightContacts];
        updatedContacts[editingIndex.index] = newContact;
        setRightContacts(updatedContacts);
      } else {
        setRightContacts([...rightContacts, newContact]);
      }
      setRightStart('');
      setRightEnd('');
    }
    setEditingIndex(null);
  };

  const removeContact = (side: 'left' | 'right', index: number) => {
    if (side === 'left') {
      setLeftContacts(leftContacts.filter((_, i) => i !== index));
    } else {
      setRightContacts(rightContacts.filter((_, i) => i !== index));
    }
    if (editingIndex?.side === side && editingIndex.index === index) {
      setEditingIndex(null);
    }
  };

  const editContact = (side: 'left' | 'right', index: number) => {
    const contacts = side === 'left' ? leftContacts : rightContacts;
    const contact = contacts[index];
    if (side === 'left') {
      setLeftStart(contact.start.toString());
      setLeftEnd(contact.end.toString());
    } else {
      setRightStart(contact.start.toString());
      setRightEnd(contact.end.toString());
    }
    setEditingIndex({ side, index });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Format the foot contact data with sorted contacts
      const footContactData = {
        leftFoot: sortedLeftContacts,
        rightFoot: sortedRightContacts,
      };

      // Submit the feedback with the foot contact data as the reason
      const { error } = await postBrowseFeedback({
        jobID: browseItem.jobID,
        sentiment: BrowseFeedbackSentiment.FOOT_CONTACT,
        reason: JSON.stringify(footContactData),
      });

      if (error) {
        throw error;
      }

      // Add the job to the foot contacts list
      addFootContacts(browseItem.jobID);

      // Call the callback if provided
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted();
      }

      // Show success message
      noticesStore.info('Foot contact data submitted successfully');
    } catch (error) {
      console.error('Error submitting foot contacts:', error);
      noticesStore.error('Failed to submit foot contacts');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`grid grid-cols-2 gap-x-8 gap-y-4 ${className}`}>
      {/* Submit Button */}

      {/* Left Column */}
      <div className="flex flex-col gap-4">
        <Text variant="studio/body/medium/md">Left Foot</Text>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Input
              type="number"
              name="leftStart"
              placeholder="Start frame"
              value={leftStart}
              onChange={(e) => setLeftStart(e.target.value)}
              className="flex-1"
              disabled={!isValidRate}
            />
            <Input
              type="number"
              name="leftEnd"
              placeholder="End frame"
              value={leftEnd}
              onChange={(e) => setLeftEnd(e.target.value)}
              className="flex-1"
              disabled={!isValidRate}
            />
            <Button
              variant="secondary"
              onClick={() => isValidRate && addContact('left')}
              disabled={!isValidRate || isSubmitting}
            >
              {editingIndex?.side === 'left' ? 'Update' : 'Add'}
            </Button>
          </div>
          <div className="space-y-2">
            {sortedLeftContacts.map((contact, index) => (
              <div key={index} className="flex items-center gap-2">
                <Text variant="studio/body/md">
                  {contact.start} - {contact.end}
                </Text>
                <Button
                  variant="tertiary"
                  onClick={() => editContact('left', index)}
                  disabled={isSubmitting}
                >
                  Edit
                </Button>
                <Button
                  variant="tertiary"
                  onClick={() => removeContact('left', index)}
                  disabled={isSubmitting}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="flex flex-col gap-4">
        <Text variant="studio/body/medium/md">Right Foot</Text>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Input
              type="number"
              name="rightStart"
              placeholder="Start frame"
              value={rightStart}
              onChange={(e) => setRightStart(e.target.value)}
              className="flex-1"
              disabled={!isValidRate}
            />
            <Input
              type="number"
              name="rightEnd"
              placeholder="End frame"
              value={rightEnd}
              onChange={(e) => setRightEnd(e.target.value)}
              className="flex-1"
              disabled={!isValidRate}
            />
            <Button
              variant="secondary"
              onClick={() => isValidRate && addContact('right')}
              disabled={!isValidRate || isSubmitting}
            >
              {editingIndex?.side === 'right' ? 'Update' : 'Add'}
            </Button>
          </div>
          <div className="space-y-2">
            {sortedRightContacts.map((contact, index) => (
              <div key={index} className="flex items-center gap-2">
                <Text variant="studio/body/md">
                  {contact.start} - {contact.end}
                </Text>
                <Button
                  variant="tertiary"
                  onClick={() => editContact('right', index)}
                  disabled={isSubmitting}
                >
                  Edit
                </Button>
                <Button
                  variant="tertiary"
                  onClick={() => removeContact('right', index)}
                  disabled={isSubmitting}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="col-span-2">
        <Button
          variant="primary"
          onClick={() => isValidRate && handleSubmit()}
          disabled={!isValidRate || isSubmitting}
          className="w-full"
        >
          Submit
        </Button>
      </div>
    </div>
  );
}
